import { getWorkflowMemory } from '@/redux/actions/gravityActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import workflowScreenSelectors from '../redux/selectors/workflowScreenSelectors.js';

// Requests in flight by memory scope. The loader flag is only set once the request leaves
// nonBlockingWaitToBeLoaded, so several memory surfaces mounting in the same commit would
// otherwise each fire the same enumeration.
const inFlightScopes = new Map();

/**
 * Lazily loads the workflow's persisted memory for the active environment/installation scope.
 * The primary workflow read only hydrates memory the initially rendered editor depends on, so
 * every surface that displays or offers persisted memory values must ask for it.
 */
const useWorkflowMemory = ({ enabled = true } = {}) => {
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const environmentId = useSelector((state) => workflowScreenSelectors.activeWorkflowEnvironment(state)?.id);
  const installationId = useSelector(workflowScreenSelectors.activeWorkflowTestInstallationId);
  const loadedScope = useSelector(selectors.activeWorkflowLoadedMemoryScope);
  const isLoading = useArgSelector(selectors.isLoading, 'getWorkflowMemory');

  // A selection the loaded enumeration was not resolved for is stale memory, not loaded memory.
  // Only compare a selection the editor has actually resolved.
  const matchesLoadedScope =
    (!environmentId || environmentId === loadedScope.environmentId) && //
    (!installationId || installationId === loadedScope.installationId);
  const needsMemory = enabled && !!workflowId && loadedScope.hasWorkflow && (!loadedScope.isLoaded || !matchesLoadedScope);
  const requestScope = [workflowId, environmentId, installationId].filter(Boolean).join(':');

  useEffect(() => {
    if (!needsMemory || inFlightScopes.has(requestScope)) {
      return;
    }

    const request = getWorkflowMemory({ workflowId, environmentId, installationId }).finally(() => inFlightScopes.delete(requestScope));
    inFlightScopes.set(requestScope, request);
  }, [needsMemory, requestScope, workflowId, environmentId, installationId]);

  return { isLoading: isLoading && needsMemory };
};

export default useWorkflowMemory;
